import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchScoringRulesByQuiz,
  saveScoringRulesForAnswer,
  bulkUpdateScoringRules,
  previewScoringResults,
  renameQuestionKey,
  type BulkUpdateGroup,
} from "@/services/scoring-rules";
import type {
  SaveScoringRulesRequest,
  ScoringPreviewResponse,
} from "@/types/scoring-rules";

export function useScoringRules(quizId: string | undefined) {
  return useQuery({
    queryKey: ["scoring-rules", quizId],
    queryFn: () => fetchScoringRulesByQuiz(quizId!),
    enabled: !!quizId,
  });
}

export function useSaveScoringRules(quizId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      answerId,
      body,
    }: {
      answerId: string;
      body: SaveScoringRulesRequest;
    }) => saveScoringRulesForAnswer(answerId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scoring-rules", quizId] });
    },
  });
}

export function useBulkUpdateScoringRules(quizId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (updates: BulkUpdateGroup[]) =>
      bulkUpdateScoringRules(quizId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scoring-rules", quizId] });
    },
  });
}

export function useScoringPreviewMutation(quizId: string) {
  return useMutation<
    ScoringPreviewResponse,
    Error,
    { selectedAnswers: Record<string, string>; limit?: number }
  >({
    mutationFn: ({ selectedAnswers, limit }) =>
      previewScoringResults(quizId, selectedAnswers, limit),
  });
}

export function useRenameQuestionKey(quizId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ oldKey, newKey }: { oldKey: string; newKey: string }) =>
      renameQuestionKey(quizId, oldKey, newKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scoring-rules", quizId] });
      queryClient.invalidateQueries({
        queryKey: ["admin", "scoring-preview", quizId],
      });
    },
  });
}
